/** @jsx jsx */
import {
  Button,
  Flex,
  Heading,
  Paragraph,
  PencilIcon,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "@eworkit/iris";
import { format } from "date-fns";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { DownloadLink } from "@/components/Files";
import { NoData } from "@/components/no-data";
import { TimeReportDayAggregate } from "@/data/time-reports";
import { useFormats } from "@/hooks/Formats";
import { useDateFnsLocale } from "@/utils/dates";

import { useTimeReportEdit } from "../../edit/TimeReportEditContext";

interface TimeReportSpecificationDayProps {
  date: Date;
  dayAggregateData: TimeReportDayAggregate;
  editable?: boolean;
}

export const TimeReportSpecificationDay: FC<TimeReportSpecificationDayProps> = ({
  date,
  dayAggregateData,
  editable,
}) => {
  const locale = useDateFnsLocale();
  const { formatDate } = useFormats();

  const { showSpecificationModal } = useTimeReportEdit();

  const { t } = useTranslation("session");

  return (
    <Flex flexDirection="column" gap="sm">
      <Flex gap="xs">
        <Heading size="h-xs" color="n1000">
          {formatDate(date.toISOString())}
        </Heading>
        <Heading size="h-xs" color="n700" sx={{ textTransform: "capitalize" }}>
          {format(date, "EEEE", { locale })}
        </Heading>
      </Flex>

      <Table>
        <TableHeader>
          <TableHeaderCell width="22%">{t("TIME_REPORTS.SPECIFICATION.PROJECT")}</TableHeaderCell>
          <TableHeaderCell width="17%">{t("TIME_REPORTS.SPECIFICATION.PAYCODE")}</TableHeaderCell>
          <TableHeaderCell width="8%">{t("TIME_REPORTS.SPECIFICATION.TIME")}</TableHeaderCell>
          <TableHeaderCell width="27%">{t("TIME_REPORTS.SPECIFICATION.COMMENT")}</TableHeaderCell>
          <TableHeaderCell width="21%">{t("TIME_REPORTS.SPECIFICATION.ATTACHMENTS")}</TableHeaderCell>
          <TableHeaderCell />
        </TableHeader>

        <TableBody>
          {dayAggregateData.projects.map(project =>
            project.payCodes.map((payCode, payCodeIndex) =>
              payCode.items
                .filter(item => Boolean(item.item))
                .map(item => (
                  <TableRow key={item.id}>
                    {payCodeIndex === 0 && (
                      <TableCell
                        headerLabel={t("TIME_REPORTS.SPECIFICATION.PROJECT")}
                        rowSpan={project.payCodes.length}>
                        <Paragraph>{project.name}</Paragraph>
                      </TableCell>
                    )}
                    <TableCell headerLabel={t("TIME_REPORTS.SPECIFICATION.PAYCODE")}>
                      <Paragraph>{payCode.name}</Paragraph>
                    </TableCell>
                    <TableCell headerLabel={t("TIME_REPORTS.SPECIFICATION.TIME")}>
                      <Paragraph>{item.loggedTime.formattedAmount}</Paragraph>
                    </TableCell>
                    <TableCell headerLabel={t("TIME_REPORTS.SPECIFICATION.COMMENT")}>
                      {item.comment ? <Paragraph>{item.comment}</Paragraph> : <NoData />}
                    </TableCell>
                    <TableCell headerLabel={t("TIME_REPORTS.SPECIFICATION.ATTACHMENTS")}>
                      {item.attachments?.length ? (
                        <Flex flexDirection="column">
                          {item.attachments?.map((attachment, index) => (
                            <DownloadLink key={index} attachment={attachment}>
                              {attachment.name}
                            </DownloadLink>
                          ))}
                        </Flex>
                      ) : (
                        <NoData />
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        "& > [data-component='table-cell-container']": {
                          py: "xs",
                        },
                      }}>
                      {editable && (
                        <Button
                          size="sm"
                          variant="ghost"
                          iconLeft={<PencilIcon />}
                          onClick={() => showSpecificationModal(item.itemPath)}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                )),
            ),
          )}
        </TableBody>
      </Table>
    </Flex>
  );
};
